import { useEffect, useState } from 'react';
import { apiFetch } from '../api';
import DmsAcknowledgeModal from '../pages/dms/DmsAcknowledgeModal';
import { useDarkMode } from '../contexts/DarkModeContext';

interface DmsDocument {
  id: string;
  series_id: string;
  folder_id: string | null;
  folder_name?: string;
  title: string;
  document_number: string | null;
  description: string | null;
  version: string;
  file_name: string;
  mime_type: string;
  target_type: 'all' | 'specific';
  is_mandatory_acknowledgement: boolean;
  is_important_popup: boolean;
  status: 'active' | 'superseded' | 'archived';
  is_current_version: boolean;
  valid_from: string;
  valid_until: string | null;
}

export default function DmsUrgentModal(): JSX.Element | null {
  const [urgentDocs, setUrgentDocs] = useState<DmsDocument[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [acknowledgingDoc, setAcknowledgingDoc] = useState<DmsDocument | null>(null);
  const [dismissed, setDismissed] = useState(false);

  const isDark = (() => {
    try {
      const dm = useDarkMode();
      return dm.isDarkMode;
    } catch {
      return document.documentElement.classList.contains('dark');
    }
  })();

  const theme = {
    cardBg: isDark ? '#1e293b' : '#ffffff',
    innerBg: isDark ? '#0f172a' : '#f8fafc',
    border: isDark ? '#334155' : '#e2e8f0',
    textMain: isDark ? '#f8fafc' : '#0f172a',
    textMuted: isDark ? '#94a3b8' : '#64748b',
    textSub: isDark ? '#cbd5e1' : '#334155',
  };

  useEffect(() => {
    // Nur laden, wenn Benutzer eingeloggt ist
    const token = localStorage.getItem('token');
    if (!token) return;

    checkPendingUrgent();
  }, []);

  async function checkPendingUrgent() {
    try {
      const docs = await apiFetch('/dms/my-pending-urgent');
      if (Array.isArray(docs) && docs.length > 0) {
        setUrgentDocs(docs);
        setCurrentIndex(0);
      } else {
        setUrgentDocs([]);
      }
    } catch (err) {
      // Ignorieren falls nicht verfügbar oder 401
    }
  }

  async function handleDownloadDocument(e: React.MouseEvent, docId: string) {
    e.preventDefault();
    const token = localStorage.getItem('token') || '';
    try {
      const res = await fetch(`/api/dms/documents/${docId}/download`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        throw new Error('Download fehlgeschlagen');
      }
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      window.open(url, '_blank');
    } catch (err) {
      window.open(`/api/dms/documents/${docId}/download?token=${encodeURIComponent(token)}`, '_blank');
    }
  }

  if (dismissed || urgentDocs.length === 0) {
    return null;
  }

  const currentDoc = urgentDocs[currentIndex] || urgentDocs[0];

  return (
    <>
      <div
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.75)',
          zIndex: 9998,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '20px',
        }}
      >
        <div
          style={{
            backgroundColor: theme.cardBg,
            color: theme.textMain,
            borderRadius: '16px',
            width: '100%',
            maxWidth: '650px',
            border: '2px solid #ef4444',
            boxShadow: isDark
              ? '0 25px 50px -12px rgba(239, 68, 68, 0.35)'
              : '0 20px 25px -5px rgba(239, 68, 68, 0.2), 0 8px 10px -6px rgba(0, 0, 0, 0.1)',
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          {/* Urgent Header Banner */}
          <div
            style={{
              padding: '20px 24px',
              backgroundColor: '#dc2626',
              color: '#fff',
              display: 'flex',
              alignItems: 'center',
              gap: '14px',
            }}
          >
            <span style={{ fontSize: '2rem' }}>🚨</span>
            <div>
              <h2 style={{ margin: 0, fontSize: '1.3rem', fontWeight: 800 }}>
                Wichtige Kenntnisnahme erforderlich!
              </h2>
              <div style={{ fontSize: '0.9rem', opacity: 0.95, marginTop: '2px' }}>
                Es liegt eine neue verbindliche Weisung / Sicherheitsunterweisung für Sie vor.
                {urgentDocs.length > 1 && ` (Dokument ${currentIndex + 1} von ${urgentDocs.length})`}
              </div>
            </div>
          </div>

          {/* Body */}
          <div style={{ padding: '24px', display: 'flex', flexDirection: 'column', gap: '16px' }}>
            <div
              style={{
                backgroundColor: theme.innerBg,
                padding: '18px',
                borderRadius: '10px',
                border: `1px solid ${theme.border}`,
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                {currentDoc.document_number && (
                  <span
                    style={{
                      backgroundColor: '#ef4444',
                      color: '#fff',
                      padding: '2px 8px',
                      borderRadius: '4px',
                      fontSize: '0.8rem',
                      fontWeight: 700,
                    }}
                  >
                    {currentDoc.document_number}
                  </span>
                )}
                <span
                  style={{
                    backgroundColor: isDark ? '#334155' : '#e2e8f0',
                    color: isDark ? '#94a3b8' : '#475569',
                    padding: '2px 8px',
                    borderRadius: '4px',
                    fontSize: '0.8rem',
                    fontWeight: 600,
                  }}
                >
                  Version {currentDoc.version}
                </span>
                {currentDoc.folder_name && (
                  <span style={{ color: isDark ? '#38bdf8' : '#0284c7', fontSize: '0.85rem', fontWeight: 500 }}>
                    📁 {currentDoc.folder_name}
                  </span>
                )}
              </div>

              <h3 style={{ margin: '0 0 10px 0', fontSize: '1.3rem', color: theme.textMain }}>
                {currentDoc.title}
              </h3>

              <div style={{ fontSize: '0.9rem', color: theme.textSub, lineHeight: 1.5 }}>
                {currentDoc.description || 'Keine Beschreibung hinterlegt.'}
              </div>

              <div style={{ marginTop: '12px', fontSize: '0.8rem', color: theme.textMuted }}>
                📅 Gültig ab: {currentDoc.valid_from} {currentDoc.valid_until ? `bis ${currentDoc.valid_until}` : ''}
              </div>
            </div>

            <div
              style={{
                backgroundColor: isDark ? 'rgba(239, 68, 68, 0.1)' : '#fef2f2',
                border: isDark ? '1px solid rgba(239, 68, 68, 0.3)' : '1px solid #fecaca',
                padding: '12px 16px',
                borderRadius: '8px',
                fontSize: '0.85rem',
                color: isDark ? '#fca5a5' : '#b91c1c',
                lineHeight: 1.4,
              }}
            >
              ⚠️ Dieses Dokument wurde von der Organisationsleitung als <strong>dringend verpflichtend</strong> eingestuft. Bitte lesen Sie den Inhalt aufmerksam durch und bestätigen Sie die Kenntnisnahme digital mit Ihrem Anmeldekennwort.
            </div>

            {/* Action Buttons */}
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginTop: '12px',
                flexWrap: 'wrap',
                gap: '12px',
              }}
            >
              <button
                type="button"
                onClick={() => setDismissed(true)}
                style={{
                  background: 'transparent',
                  border: 'none',
                  color: theme.textMuted,
                  fontSize: '0.85rem',
                  cursor: 'pointer',
                  textDecoration: 'underline',
                }}
              >
                Später erinnern (Schließen)
              </button>

              <div style={{ display: 'flex', gap: '10px' }}>
                <a
                  href={`/api/dms/documents/${currentDoc.id}/download?token=${encodeURIComponent(localStorage.getItem('token') || '')}`}
                  onClick={(e) => handleDownloadDocument(e, currentDoc.id)}
                  target="_blank"
                  rel="noreferrer"
                  style={{
                    backgroundColor: '#0284c7',
                    color: '#fff',
                    padding: '10px 16px',
                    borderRadius: '8px',
                    textDecoration: 'none',
                    fontWeight: 600,
                    fontSize: '0.9rem',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '6px',
                  }}
                >
                  👁️ Dokument öffnen
                </a>

                <button
                  type="button"
                  onClick={() => setAcknowledgingDoc(currentDoc)}
                  style={{
                    backgroundColor: '#16a34a',
                    color: '#fff',
                    border: 'none',
                    padding: '10px 22px',
                    borderRadius: '8px',
                    fontWeight: 700,
                    fontSize: '0.95rem',
                    cursor: 'pointer',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '8px',
                    boxShadow: '0 4px 6px -1px rgba(22, 163, 74, 0.4)',
                  }}
                >
                  ✍️ Jetzt lesen & abzeichnen
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {acknowledgingDoc && (
        <DmsAcknowledgeModal
          document={acknowledgingDoc}
          onClose={() => setAcknowledgingDoc(null)}
          onSuccess={() => {
            setAcknowledgingDoc(null);
            checkPendingUrgent();
          }}
        />
      )}
    </>
  );
}
